import { importLibrary } from "@googlemaps/js-api-loader";
import { CATEGORIES, MAP_DEFAULT_CONFIG } from "./consts.js";
import {
    createPinElement,
    setPinActive,
} from "./markerPin.js";
import { layoutPins } from "./pinSpread.js";
import PropertyService from "./propertyService.js";

class MarkerManager {
    constructor(map, stateManager, cardPanel) {
        this.map = map;
        this.stateManager = stateManager;
        this.cardPanel = cardPanel;

        // カテゴリーごとにマーカーを管理するためのオブジェクト
        this.categoryMarkers = Object.values(CATEGORIES).reduce(
            (acc, category) => {
                acc[category] = [];
                return acc;
            },
            {}
        );

        // 選択中の物件ID
        this.activeId = null;

        // 表示中の物件（県ぶん全部）と、ピンを並べたときのズーム。
        // 物件は開いたときに1回だけ受け取り、ズームが変わったら同座標のずらし幅だけ並べ直す
        this.properties = [];
        this.layoutZoom = null;

        // マーカー生成は同期で済ませたいので、読み込んだクラスをここへ退避しておく
        this.AdvancedMarkerElement = null;
    }

    // marker ライブラリの読み込み
    async init() {
        const { AdvancedMarkerElement } = await importLibrary("marker");
        this.AdvancedMarkerElement = AdvancedMarkerElement;
    }

    // 物件を受け取ってピンを立てる（地図を開いたときの1回）
    setProperties(properties) {
        this.properties = (properties ?? []).filter((property) =>
            this.#hasGeopoint(property)
        );
        this.#render();
    }

    // ズームが変わっていればピンを並べ直す（地図の idle ごとに呼ばれる）。
    // 同座標のずらし幅は画面上の px で一定なので、ズームが変わると緯度経度での位置が変わる。
    // 移動だけなら位置は変わらないので作り直さない
    relayoutIfZoomChanged() {
        if (this.map.getZoom() === this.layoutZoom) return;
        this.#render();
    }

    // どのズームでも1物件1ピン（件数ピンへの集約はしない）。
    // 同座標の物件（分譲地の複数区画など）は重なって押せなくなるので、
    // 現在のズームで画面上一定の距離だけずらす
    #render() {
        this.clearMarkers();

        const zoom = this.map.getZoom() ?? MAP_DEFAULT_CONFIG.zoom;
        this.layoutZoom = this.map.getZoom();
        layoutPins(this.properties, zoom).forEach(({ property, position }) =>
            this.#createMarker(property, position)
        );
    }

    // マーカーをクリア
    clearMarkers() {
        Object.values(this.categoryMarkers).forEach((markers) => {
            markers.forEach((marker) => {
                // 破棄するマーカーのリスナーが残らないようにする
                google.maps.event.clearInstanceListeners(marker);
                marker.map = null;
            });
        });

        Object.keys(this.categoryMarkers).forEach((category) => {
            this.categoryMarkers[category] = [];
        });
    }

    // カードが閉じられたときに選択表示を解除する
    clearActiveMarker() {
        this.activeId = null;
        this.#syncActiveMarkers();
    }

    #hasGeopoint(property) {
        return (
            Boolean(property?.geopoint) &&
            Number.isFinite(Number(property.geopoint.lat)) &&
            Number.isFinite(Number(property.geopoint.lng))
        );
    }

    // マーカーを作成して地図へ載せる。position は同座標をずらした後の表示位置
    #createMarker(property, position) {
        const category = this.#resolveCategory(property);
        const content = createPinElement(property);
        setPinActive(content, property.id === this.activeId);

        const marker = new this.AdvancedMarkerElement({
            position,
            content,
            // カスタムcontentのマーカーは既定でクリック不可のため明示的に有効化する
            gmpClickable: true,
        });
        marker.propertyId = property.id;
        marker.map = this.map;

        marker.addListener("gmp-click", () =>
            this.#handleMarkerClick(property, position)
        );

        this.categoryMarkers[category].push(marker);

        // 選択中の物件は、カードの指し先を作り直したピンの位置へ合わせる
        // （ズームでずらし幅が変わると、元の位置を指したままになるため）
        if (property.id === this.activeId) this.cardPanel.moveAnchor(position);
    }

    // 未知のカテゴリーでもマーカーが消えないよう、バケットを作って受け止める
    #resolveCategory(property) {
        const category = property.category_type;
        if (!this.categoryMarkers[category]) {
            this.categoryMarkers[category] = [];
        }
        return category;
    }

    // 選択中ピンだけ見た目を差し替える（全再生成しない＝カクつき防止）
    #syncActiveMarkers() {
        Object.values(this.categoryMarkers).forEach((markers) => {
            markers.forEach((marker) => {
                setPinActive(
                    marker.content,
                    marker.propertyId === this.activeId
                );
            });
        });
    }

    // マーカークリック時の処理
    async #handleMarkerClick(property, position) {
        // ピン押下に続く地図クリックで即閉じしないよう抑止する
        this.cardPanel.suppressMapClick();

        if (this.activeId === property.id && this.cardPanel.isOpen()) return;

        this.activeId = property.id;
        this.#syncActiveMarkers();

        // カードは押したピンの表示位置を指す。同座標でずらしたピンでも、
        // 真の位置を指すと隣のピンを押したように見えてしまうため
        const token = this.cardPanel.open(position);

        try {
            const html = await PropertyService.fetchPropertiesByIds(
                [property.id],
                this.stateManager.getTransactionType()
            );
            this.cardPanel.setContent(html, token);
        } catch (error) {
            console.error("物件カードの取得に失敗しました:", error);
            this.cardPanel.setMessage("データの取得に失敗しました", token);
        }
    }
}

export default MarkerManager;
